import { createDarkTheme, createLightTheme, themeToTokensObject, } from '@fluentui/react-components';
import { brandRamp, complianceColors, statusTints, elevation, fontFamily, fontSize, fontWeight, lineHeight, radius, spacing, } from './tokens.generated.js';
/**
 * The Fluent wiring for our design tokens. Values come from tokens.generated.ts
 * (which comes from tokens.json); this file only decides how they map onto
 * Fluent's theme. It is hand-written on purpose — the mapping is a design
 * decision and should show up in review.
 */
const brand = {
    10: brandRamp['10'],
    20: brandRamp['20'],
    30: brandRamp['30'],
    40: brandRamp['40'],
    50: brandRamp['50'],
    60: brandRamp['60'],
    70: brandRamp['70'],
    80: brandRamp['80'],
    90: brandRamp['90'],
    100: brandRamp['100'],
    110: brandRamp['110'],
    120: brandRamp['120'],
    130: brandRamp['130'],
    140: brandRamp['140'],
    150: brandRamp['150'],
    160: brandRamp['160'],
};
/**
 * Fluent's type ramp, spacing, radii and elevation are plain theme keys, so the
 * whole scale is overridden here rather than component by component.
 */
const scale = {
    spacingHorizontalXXS: spacing.xxs,
    spacingHorizontalXS: spacing.xs,
    spacingHorizontalSNudge: spacing.sNudge,
    spacingHorizontalS: spacing.s,
    spacingHorizontalMNudge: spacing.mNudge,
    spacingHorizontalM: spacing.m,
    spacingHorizontalL: spacing.l,
    spacingHorizontalXL: spacing.xl,
    spacingHorizontalXXL: spacing.xxl,
    spacingHorizontalXXXL: spacing.xxxl,
    spacingVerticalXXS: spacing.xxs,
    spacingVerticalXS: spacing.xs,
    spacingVerticalSNudge: spacing.sNudge,
    spacingVerticalS: spacing.s,
    spacingVerticalMNudge: spacing.mNudge,
    spacingVerticalM: spacing.m,
    spacingVerticalL: spacing.l,
    spacingVerticalXL: spacing.xl,
    spacingVerticalXXL: spacing.xxl,
    spacingVerticalXXXL: spacing.xxxl,
    borderRadiusSmall: radius.small,
    borderRadiusMedium: radius.medium,
    borderRadiusLarge: radius.large,
    borderRadiusXLarge: radius.xLarge,
    fontSizeBase100: fontSize.base100,
    fontSizeBase200: fontSize.base200,
    fontSizeBase300: fontSize.base300,
    fontSizeBase400: fontSize.base400,
    fontSizeBase500: fontSize.base500,
    fontSizeBase600: fontSize.base600,
    fontSizeHero700: fontSize.hero700,
    fontSizeHero800: fontSize.hero800,
    fontSizeHero900: fontSize.hero900,
    fontSizeHero1000: fontSize.hero1000,
    lineHeightBase100: lineHeight.base100,
    lineHeightBase200: lineHeight.base200,
    lineHeightBase300: lineHeight.base300,
    lineHeightBase400: lineHeight.base400,
    lineHeightBase500: lineHeight.base500,
    lineHeightBase600: lineHeight.base600,
    lineHeightHero700: lineHeight.hero700,
    lineHeightHero800: lineHeight.hero800,
    lineHeightHero900: lineHeight.hero900,
    lineHeightHero1000: lineHeight.hero1000,
    fontWeightRegular: fontWeight.regular,
    fontWeightMedium: fontWeight.medium,
    fontWeightSemibold: fontWeight.semibold,
    fontWeightBold: fontWeight.bold,
    fontFamilyBase: fontFamily.base,
    fontFamilyMonospace: fontFamily.monospace,
    fontFamilyNumeric: fontFamily.numeric,
    shadow2: elevation.shadow2,
    shadow4: elevation.shadow4,
    shadow8: elevation.shadow8,
    shadow16: elevation.shadow16,
    shadow28: elevation.shadow28,
    shadow64: elevation.shadow64,
};
/**
 * Brand 80 (#2a8c69) measures 4.16:1 against white — below the 4.5:1 WCAG AA asks of normal
 * text — both as text on a white surface and as white text on a brand surface, and the
 * light theme's brand aliases all resolve to it. These aliases point one rung darker
 * (brand 70, #1e7a5a: 5.27:1 both ways; hover/pressed a rung darker again). The ramp itself
 * is untouched and stays Figma-shaped; the dark theme already measures 5.31:1 and is left alone.
 * Measured by the browser QA (docs/mvp/evidence/e2e/browser-qa.jsonl), recorded in
 * docs/mvp/design-integration.md.
 */
const accessibleBrand = {
    colorBrandForeground1: brandRamp['70'],
    colorBrandForeground2: brandRamp['60'],
    colorBrandForegroundLink: brandRamp['70'],
    colorBrandForegroundLinkHover: brandRamp['60'],
    colorBrandForegroundLinkPressed: brandRamp['50'],
    colorBrandBackground: brandRamp['70'],
    colorBrandBackgroundHover: brandRamp['60'],
    colorBrandBackgroundPressed: brandRamp['40'],
    colorBrandBackgroundSelected: brandRamp['60'],
    colorCompoundBrandForeground1: brandRamp['70'],
    colorCompoundBrandForeground1Hover: brandRamp['60'],
    colorCompoundBrandBackground: brandRamp['70'],
    colorCompoundBrandBackgroundHover: brandRamp['60'],
    colorCompoundBrandStroke: brandRamp['70'],
    colorBrandStroke1: brandRamp['70'],
};
/**
 * The current page in the side menu (#742). Designer 404's arithmetic on #715, not a browser measurement: the
 * name on the band is 5.44:1 light and 5.62:1 dark, the bar on the band 4.44:1 and 3.89:1. The band against the
 * menu is faint on purpose (1.19:1 and 1.37:1), which is why the bar and the heavier name are there too.
 */
const navActive = {
    light: { background: brandRamp['150'], foreground: brandRamp['60'], marker: brandRamp['70'] },
    dark: { background: brandRamp['40'], foreground: brandRamp['120'], marker: brandRamp['100'] },
};
function withVigeoTokens(base, scheme) {
    return {
        ...base,
        ...(scheme === 'light' ? accessibleBrand : {}),
        ...scale,
        colorComplianceGood: complianceColors[scheme].good,
        colorComplianceWarning: complianceColors[scheme].warning,
        colorComplianceCritical: complianceColors[scheme].critical,
        colorComplianceAttention: complianceColors[scheme].attention,
        colorStatusTintGood: scheme === 'light' ? statusTints.good : base.colorNeutralBackground3,
        colorStatusTintWarning: scheme === 'light' ? statusTints.warning : base.colorNeutralBackground3,
        colorStatusTintCritical: scheme === 'light' ? statusTints.critical : base.colorNeutralBackground3,
        colorStatusTintAttention: scheme === 'light' ? statusTints.attention : base.colorNeutralBackground3,
        shadowCard: elevation.shadowCard,
        colorNavActiveBackground: navActive[scheme].background,
        colorNavActiveForeground: navActive[scheme].foreground,
        colorNavActiveMarker: navActive[scheme].marker,
        colorNavBadgeBackground: complianceColors[scheme].attention,
        colorNavBadgeForeground: scheme === 'light' ? base.colorNeutralForegroundOnBrand : base.colorNeutralBackground1,
    };
}
// Hoisted at module scope: FluentProvider re-serializes every theme key when the
// theme's identity changes, so these must be stable across renders.
export const lightTheme = withVigeoTokens(createLightTheme(brand), 'light');
export const darkTheme = withVigeoTokens(createDarkTheme(brand), 'dark');
/**
 * The token accessor every component uses. Each value is a `var(--token)` string,
 * so it resolves against whichever theme FluentProvider is currently rendering —
 * deriving it from the light theme does not pin it to light.
 *
 * Import this from "@vigeo/ui" rather than Fluent's own `tokens`: it carries the
 * compliance tokens too, and is typed to our theme. ESLint enforces that.
 */
export const tokens = themeToTokensObject(lightTheme);
/** Compliance tiers in severity order, for legends and status rendering. */
/**
 * `attention` is not a severity between warning and critical — it is a different AXIS.
 * good/warning/critical say how bad the compliance position is; attention says the ball is
 * on HR's desk. "Ready with exceptions" and "Needs review" are operationally opposite —
 * one person may start work, the other may not — so they must not share a colour.
 */
export const complianceTones = ['good', 'warning', 'critical', 'attention'];
export const complianceTokenFor = {
    good: tokens.colorComplianceGood,
    warning: tokens.colorComplianceWarning,
    critical: tokens.colorComplianceCritical,
    attention: tokens.colorComplianceAttention,
};
//# sourceMappingURL=tokens.js.map